import { useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import NavMarker from 'react-rhumb/NavMarker';
import Raven from 'raven-js';
import { useMutation } from 'data-fetching-client';
import { parse as parseParams } from 'hub-http/helpers/params';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Loadable from 'UIComponents/decorators/Loadable';
import { getDataFetchingClientRequestState } from 'marketplace-ui-common/dataFetchingClient/utils/getDataFetchingClientRequestState';
import * as RequestState from 'marketplace-ui-common/requestStates/RequestState';
import * as ReviewType from 'marketplace-ui-reviews/constants/ReviewType';
import Review from 'marketplace-ui-reviews/records/Review';
import { RatingAxes } from 'marketplace-ui-reviews/constants/RatingAxes';
import getDisplayedReviewerName from 'marketplace-ui-reviews/utils/getDisplayedReviewerName';
import { PENDING } from 'marketplace-ui-reviews/constants/UnreviewableReason';
import { SUBMIT_REVIEW } from 'marketplace-ui-reviews/queries/submitReviewQuery';
import { getUserEmail, getUserFirstName, getUserLastName
// @ts-expect-error untyped dependency
} from '../../../selectors/auth';
// @ts-expect-error untyped dependency
import { postNotification as postUnreviewableAlert } from 'ReduxMessenger/actions/NotificationActions';
import { UNREVIEWABLE_ALERT_ID
// @ts-expect-error untyped dependency
} from '../../../constants/Reviewability';
// @ts-expect-error untyped dependency
import { getIsUngatedForPrivateFeedback } from '../../../selectors/auth';
import { jsx as _jsx } from "react/jsx-runtime";
const AsyncReviewWizard = Loadable({
  loader: () => import( /* webpackChunkName: 'review-wizard' */'marketplace-ui-reviews/components/wizard/AppReviewWizard')
});
export default function ReviewWizard({
  appName,
  appIcon,
  offeringId,
  onClose,
  onSuccess,
  onTrackInteraction,
  slug,
  supportLink,
  unreviewableReason
}) {
  const isUngatedForPrivateFeedback = useSelector(getIsUngatedForPrivateFeedback);
  const firstName = useSelector(getUserFirstName);
  const lastName = useSelector(getUserLastName);
  const userEmail = useSelector(getUserEmail);
  const [submitReview, {
    loading: reviewWriteLoading,
    error: reviewWriteError
  }] = useMutation(SUBMIT_REVIEW);
  const reviewWriteStatus = useMemo(() => getDataFetchingClientRequestState(reviewWriteError, reviewWriteLoading), [reviewWriteError, reviewWriteLoading]);
  const dispatch = useDispatch();
  const displayName = getDisplayedReviewerName({
    firstName,
    lastName
  });
  const {
    search
  } = useLocation();
  const {
    eco_review_source,
    rating
  } = parseParams(search.slice(1));
  const reviewSource = eco_review_source || 'details';
  const overallRating = /^[1-5]$/.test(rating) ? parseInt(rating, 10) : undefined;
  const defaultReview = useMemo(() => {
    let storedReview = null;
    try {
      storedReview = JSON.parse(sessionStorage.getItem(`${slug}_review`));
    } catch (__error) {
      // pass through
    }
    let displayedReview = storedReview ? new Review(storedReview) : new Review();
    let trackedOverallRating = displayedReview.ratings.get(RatingAxes.OVERALL);
    if (overallRating) {
      trackedOverallRating = overallRating;
      displayedReview = displayedReview.setOverallRating(overallRating);
    }
    onTrackInteraction('set overall rating', 'review', trackedOverallRating);
    return displayedReview;
  }, [onTrackInteraction, overallRating, slug]);
  const handleSubmit = useCallback(review => new Promise((resolve, reject) => {
    sessionStorage.removeItem(`${slug}_review`);
    onTrackInteraction('submit review', 'review', undefined, {
      reviewSource
    });
    //
    submitReview({
      variables: {
        review: review.set('createdAt', new Date().valueOf()).set('entityId', offeringId).set('reviewType', ReviewType.APP)
      }
    }).then(() => {
      resolve('Successfully submitted review');
      onSuccess();
    }).catch(err => {
      Raven.captureMessage('Failed to leave a review', {
        level: 'error',
        extra: Object.assign({
          offeringId
        }, err)
      });
      reject(err);
    });
  }), [offeringId, onSuccess, onTrackInteraction, reviewSource, slug, submitReview]);
  const updateSessionStorage = useCallback(updatedReview => {
    onTrackInteraction('set overall rating', 'review', updatedReview.ratings.get(RatingAxes.OVERALL));
    sessionStorage.setItem(`${slug}_review`, JSON.stringify(updatedReview));
  }, [onTrackInteraction, slug]);
  const onWizardLoad = useCallback(() => {
    onTrackInteraction('open review wizard', 'review', undefined, {
      reviewSource
    });
  }, [onTrackInteraction, reviewSource]);
  const onStepChange = useCallback((prevStep, nextStep) => {
    onTrackInteraction('change review wizard step', 'review', nextStep, {
      prevStep,
      reviewSource
    });
  }, [onTrackInteraction, reviewSource]);
  const onReject = useCallback(() => {
    onTrackInteraction('dismiss review wizard', 'review', undefined, {
      reviewSource
    });
    onClose();
  }, [onTrackInteraction, onClose, reviewSource]);
  useEffect(() => {
    if (unreviewableReason && unreviewableReason !== `ReviewError.${PENDING}`) {
      dispatch(postUnreviewableAlert({
        id: UNREVIEWABLE_ALERT_ID,
        type: 'danger',
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "appMarketplace.detail.reviews.unreviewable.titleText"
        }),
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `marketplace-ui-reviews.error.${unreviewableReason}`
        })
      }));
    }
  }, [dispatch, unreviewableReason]);
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "REVIEW_WIZARD_LOAD",
    children: !unreviewableReason && /*#__PURE__*/_jsx(AsyncReviewWizard, {
      isUngatedForPrivateFeedback: isUngatedForPrivateFeedback,
      reviewableName: appName,
      reviewableIconUrl: appIcon,
      failed: reviewWriteStatus === RequestState.FAILED,
      defaultReview: defaultReview,
      displayName: displayName,
      loading: reviewWriteStatus === RequestState.PENDING,
      onReject: onReject,
      onChange: updateSessionStorage,
      onConfirm: handleSubmit,
      onStepChange: onStepChange,
      onWizardLoad: onWizardLoad,
      supportLink: supportLink,
      userEmail: userEmail
    })
  });
}